<template>
  <section class="telemetry-summary-report-wrapper">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="BarChart2Icon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Graphic Analysis') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Telemetry summary and charts') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center flex-wrap mt-1 mt-lg-0"
        style="gap: 8px;"
      >
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="isLoading"
          @click="refetchTelemetry"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isLoading }"
          />
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'queries-month', params: { id: assetId } }"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Monthly Summary') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'queries-telemetry', params: { id: assetId }, query: { journey: 0 } }"
        >
          <feather-icon
            icon="MapIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Way') }}</span>
        </b-button>
      </div>
    </div>

    <b-card
      no-body
      class="asset-header-card mb-1 shadow-sm"
    >
      <div class="asset-header-content p-1">
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center py-50"
        >
          <b-spinner
            variant="primary"
            small
            class="mr-1"
          />
          <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
        </div>

        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || $t('Model')"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted"
                  />
                </div>

                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || assetPlate || assetChassis || assetId }}
                  </h5>
                </div>

                <b-badge
                  v-if="assetNickname"
                  variant="light-primary"
                  class="ml-50 font-weight-normal"
                >
                  {{ assetNickname }}
                </b-badge>
              </div>

              <div
                v-if="!assetPlate && assetChassis"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis, $t('chassis'))"
                  >
                    {{ assetChassis }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <div
            class="d-flex align-items-center flex-wrap"
            style="gap: 6px;"
          >
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
            </div>

            <div
              v-if="assetGroupName"
              class="meta-chip"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <div
              v-if="assetCustomerName"
              class="meta-chip"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>

            <b-button
              v-b-tooltip.hover
              variant="outline-primary"
              size="sm"
              class="d-inline-flex align-items-center"
              :title="$t('goToAssetSummary')"
              @click="goToAssetSummary"
            >
              <feather-icon
                icon="ActivityIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('assetSummary') }}</span>
            </b-button>
          </div>
        </div>
      </div>
    </b-card>

    <transition name="slide-fade">
      <b-card
        v-if="showFilters"
        no-body
        class="mb-1 shadow-sm border-0 filters-card"
      >
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="text-primary mr-50"
            />
            <h6 class="mb-0 font-weight-bolder">
              {{ $t('Filters') }}
            </h6>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ $t('{count} active', { count: activeFiltersCount }) }}
            </b-badge>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              :title="$t('Clear Filters')"
              @click="resetDateRange"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Close Filters')"
              @click="showFilters = false"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <div
            class="mb-1 d-flex flex-wrap align-items-center"
            style="gap: 6px;"
          >
            <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
            <b-badge
              v-for="preset in periodPresets"
              :key="preset.id"
              href="javascript:void(0)"
              :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset(preset.id)"
            >
              {{ preset.label }}
            </b-badge>
          </div>

          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="4"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Start Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="flatpickrConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              lg="4"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('End Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="flatpickrConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0 mr-1">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50 text-primary flex-shrink-0"
              />
              <span>
                {{ $t('Vehicle:') }}
                <strong class="text-primary">{{ assetModel || assetPlate || assetChassis || assetId }}</strong>.
                {{ $t('Maximum of 7 days') }}
              </span>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 8px;"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetDateRange"
              >
                {{ $t('Restore Default') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                :disabled="isLoading || !dateStart || !dateEnd"
                @click="refetchTelemetry"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <div
      v-if="!showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
      <b-badge
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center"
      >
        <feather-icon
          icon="LockIcon"
          size="12"
          class="mr-25"
        />
        {{ assetModel || assetPlate || assetChassis || assetId }}
      </b-badge>
      <b-badge
        v-if="periodLabel"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="CalendarIcon"
          size="12"
          class="mr-25"
        />
        {{ periodLabel }}
      </b-badge>
      <b-link
        class="font-small-2 font-weight-bold ml-50 text-danger"
        @click="resetDateRange"
      >
        {{ $t('Clear') }}
      </b-link>
    </div>

    <b-card
      v-if="isLoading"
      class="shadow-sm border-0 mb-2 text-center py-3"
    >
      <b-spinner
        variant="primary"
        class="mb-1"
      />
      <div class="text-muted font-small-3">
        {{ $t('Loading telemetry data...') }}
      </div>
    </b-card>

    <template v-else-if="hasAnyChartData">
      <b-row
        v-for="chart in visibleCharts"
        :key="chart.title"
        class="mb-1"
      >
        <b-col>
          <telemetry-chart-line
            :is-loading="isLoading"
            :title="chart.title"
            :series-name="chart.seriesName"
            :x-values="chart.xValues"
            :y-values="chart.yValues"
            :chart-height="300"
          />
        </b-col>
      </b-row>
    </template>

    <b-card
      v-else
      class="shadow-sm border-0 text-center py-3"
    >
      <b-avatar
        variant="light-warning"
        size="64px"
        class="mb-1"
      >
        <feather-icon
          icon="BarChart2Icon"
          size="28"
          class="text-warning"
        />
      </b-avatar>
      <h4 class="font-weight-bolder mb-50">
        {{ $t('No telemetry data available') }}
      </h4>
      <p class="text-muted font-small-3 mb-2 mx-auto empty-copy">
        {{ $t('No data found for the selected date range. Try selecting a different date range or check if the device is sending telemetry data.') }}
      </p>
      <b-button
        variant="outline-primary"
        size="sm"
        class="d-inline-flex align-items-center"
        @click="resetDateRange"
      >
        <feather-icon
          icon="CalendarIcon"
          size="14"
          class="mr-50"
        />
        <span>{{ $t('Reset Date Range') }}</span>
      </b-button>
    </b-card>
  </section>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from '@vue/composition-api'
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BRow,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import store from '@/store'
import router from '@/router'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import { resolveIcon } from '@/utils/assetUtils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TelemetryChartLine from '@/views/main/reports/components/TelemetryChartLine.vue'
import reportsStoreModule from '@/views/main/reports/reportsStoreModule'

const REPORTS_STORE_MODULE_NAME = 'reports'
const MAX_RANGE_DAYS = 7

export default {
  name: 'TelemetrySummaryReport',
  components: {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    BRow,
    BSpinner,
    flatPickr,
    TelemetryChartLine,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const instance = getCurrentInstance()
    const proxy = instance && instance.proxy
    const $t = proxy && proxy.$t ? proxy.$t.bind(proxy) : value => value
    const assetId = router.currentRoute.params.id
    const fallbackAvatar = resolveIcon(null, null)

    const didRegisterReports = !store.hasModule(REPORTS_STORE_MODULE_NAME)
    if (didRegisterReports) {
      store.registerModule(REPORTS_STORE_MODULE_NAME, reportsStoreModule)
    }
    onUnmounted(() => {
      if (didRegisterReports && store.hasModule(REPORTS_STORE_MODULE_NAME)) {
        store.unregisterModule(REPORTS_STORE_MODULE_NAME)
      }
    })

    function pad(value) {
      return String(value).padStart(2, '0')
    }

    function toPickerValue(date) {
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    function startOfDay(date) {
      const copy = new Date(date)
      copy.setHours(0, 0, 0, 0)
      return copy
    }

    function buildDefaultRange() {
      const end = new Date()
      const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000)
      return {
        start: toPickerValue(start),
        end: toPickerValue(end),
      }
    }

    function rangeForPreset(preset) {
      const now = new Date()
      if (preset === 'today') {
        return { start: toPickerValue(startOfDay(now)), end: toPickerValue(now) }
      }
      if (preset === 'yesterday') {
        const day = new Date(now)
        day.setDate(day.getDate() - 1)
        const start = startOfDay(day)
        const end = new Date(start)
        end.setHours(23, 59, 0, 0)
        return { start: toPickerValue(start), end: toPickerValue(end) }
      }
      return buildDefaultRange()
    }

    function parsePickerValue(value) {
      if (value instanceof Date && !Number.isNaN(value.getTime())) return value
      const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}))?/)
      if (!match) return new Date(value)
      return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4] || 0),
        Number(match[5] || 0),
        0,
      )
    }

    function formatDisplay(value) {
      if (!value) return ''
      const [datePart, timePart = ''] = String(value).split(' ')
      const [year, month, day] = datePart.split('-')
      if (!year || !month || !day) return String(value)
      return timePart ? `${day}/${month}/${year} ${timePart}` : `${day}/${month}/${year}`
    }

    function getDateUtc(value) {
      const date = parsePickerValue(value)
      return `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC`
    }

    function validateDateRange(start, end) {
      const startDate = parsePickerValue(start)
      const endDate = parsePickerValue(end)
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false
      const diffInDays = Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      return diffInDays <= MAX_RANGE_DAYS
    }

    const defaultRange = buildDefaultRange()
    const showFilters = ref(false)
    const isLoading = ref(true)
    const loadingAsset = ref(false)
    const assetData = ref(null)
    const dateStart = ref(defaultRange.start)
    const dateEnd = ref(defaultRange.end)
    const dateRangePreset = ref('last7')
    const flatpickrConfig = {
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y-m-d H:i',
      altInput: true,
      altFormat: 'd/m/Y H:i',
      altInputClass: 'form-control form-control-sm',
      locale: getFlatpickrLocale(),
    }

    let ignoreDateChange = false
    let requestId = 0

    const chartConfigurations = reactive({
      hour: {
        title: $t('Houmeter'), seriesName: $t('Hours'), xValues: [], yValues: [],
      },
      fuelLevel: {
        title: $t('Fuel Level'), seriesName: $t('%'), xValues: [], yValues: [],
      },
      meanFuelConsumption: {
        title: $t('Fuel Consumption'), seriesName: $t('l/h'), xValues: [], yValues: [],
      },
      engineCoolantTemp: {
        title: $t('Engine Coolant Temperature'), seriesName: $t('ºC'), xValues: [], yValues: [],
      },
      hydraulicOilTemp: {
        title: $t('Engine Hydraulic Temperature'), seriesName: $t('ºC'), xValues: [], yValues: [],
      },
      transmissionTemp: {
        title: $t('Engine Transmission Temperature'), seriesName: $t('ºC'), xValues: [], yValues: [],
      },
      fuelPressure: {
        title: $t('Fuel Pressure'), seriesName: $t('kPa'), xValues: [], yValues: [],
      },
      engineOilPressure: {
        title: $t('Engine Oil Pressure'), seriesName: $t('kPa'), xValues: [], yValues: [],
      },
      turboPressure: {
        title: $t('Turbo Pressure'), seriesName: $t('kPa'), xValues: [], yValues: [],
      },
      hydraulicPressure: {
        title: $t('Hydraulic Pressure'), seriesName: $t('kPa'), xValues: [], yValues: [],
      },
      engineLoad: {
        title: $t('Engine Load'), seriesName: $t('%'), xValues: [], yValues: [],
      },
      rpm: {
        title: $t('RPM'), seriesName: 'RPM', xValues: [], yValues: [],
      },
      acl: {
        title: $t('Acceleration'), seriesName: 'G', xValues: [], yValues: [],
      },
      kmh: {
        title: $t('Speed'), seriesName: 'Kmh', xValues: [], yValues: [],
      },
    })

    const assetPlate = computed(() => assetData.value?.plate || '')
    const assetChassis = computed(() => assetData.value?.chassisNumber || '')
    const assetManufacturer = computed(() => (
      assetData.value?.manufacturerName
      || assetData.value?.brandName
      || assetData.value?.manufacturer
      || ''
    ))
    const assetModel = computed(() => {
      if (!assetData.value) return ''
      const name = assetData.value.modelIdName || assetData.value.modelName || ''
      const year = assetData.value.modelYear ? `(${assetData.value.modelYear})` : ''
      return `${name} ${year}`.trim()
    })
    const assetNickname = computed(() => assetData.value?.nickname || '')
    const assetGroupName = computed(() => assetData.value?.assetGroupName || assetData.value?.groupName || '')
    const assetCustomerName = computed(() => assetData.value?.customerName || assetData.value?.ownerName || '')
    const assetAvatar = computed(() => {
      if (!assetData.value) return fallbackAvatar
      return resolveIcon(assetData.value.assetType, assetData.value.iconUrl)
    })
    const activeFiltersCount = computed(() => (dateStart.value && dateEnd.value ? 1 : 0))
    const periodLabel = computed(() => {
      if (!dateStart.value && !dateEnd.value) return ''
      return `${formatDisplay(dateStart.value)} — ${formatDisplay(dateEnd.value)}`
    })
    const periodPresets = computed(() => ([
      { id: 'today', label: $t('Today') },
      { id: 'yesterday', label: $t('Yesterday') },
      { id: 'last7', label: $t('Last 7 days') },
    ]))

    function shouldShowChart(chartData) {
      return Array.isArray(chartData)
        && chartData.length > 0
        && chartData.some(value => value !== null && value !== 0 && value !== '0')
    }

    const visibleCharts = computed(() => Object.values(chartConfigurations).filter(chart => shouldShowChart(chart.yValues)))
    const hasAnyChartData = computed(() => visibleCharts.value.length > 0)

    function showSnackbar(props) {
      if (!proxy || !proxy.$toast) return
      proxy.$toast({
        component: ToastificationContent,
        props: {
          variant: 'danger',
          icon: 'AlertTriangleIcon',
          ...props,
        },
      })
    }

    function copyToClipboard(text, label) {
      if (!text) return
      const successTitle = `${label || $t('text')} ${$t('copiedToClipboard')}!`
      const failTitle = $t('failedToCopyToClipboard')
      const notifySuccess = () => showSnackbar({ title: successTitle, icon: 'CheckIcon', variant: 'success' })
      const notifyError = () => showSnackbar({ title: failTitle, icon: 'AlertTriangleIcon', variant: 'danger' })
      if (proxy && proxy.$copyText) {
        proxy.$copyText(text).then(notifySuccess).catch(notifyError)
        return
      }
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(notifySuccess).catch(notifyError)
      }
    }

    function handleImageError(event) {
      const image = event && event.target
      if (image) image.src = fallbackAvatar
    }

    function goBack() {
      router.back()
    }

    function goToAssetSummary() {
      if (!assetId) return
      router.push({ name: 'apps-asset-summary', params: { id: assetId } })
    }

    function setDatePreset(preset) {
      ignoreDateChange = true
      const range = rangeForPreset(preset)
      dateStart.value = range.start
      dateEnd.value = range.end
      dateRangePreset.value = preset
      setTimeout(() => {
        ignoreDateChange = false
      }, 300)
    }

    function onCustomDateChange() {
      if (ignoreDateChange) return
      dateRangePreset.value = ''
    }

    function ensureDateOrder() {
      if (dateStart.value && dateEnd.value && String(dateStart.value) > String(dateEnd.value)) {
        const swap = dateStart.value
        dateStart.value = dateEnd.value
        dateEnd.value = swap
      }
    }

    function resetCharts() {
      Object.keys(chartConfigurations).forEach(key => {
        chartConfigurations[key].xValues = []
        chartConfigurations[key].yValues = []
      })
    }

    async function fetchAsset() {
      if (!assetId) return
      loadingAsset.value = true
      try {
        const response = await store.dispatch(`${REPORTS_STORE_MODULE_NAME}/fetchAsset`, { id: assetId })
        assetData.value = response?.data || null
      } catch (e) {
        assetData.value = null
      } finally {
        loadingAsset.value = false
      }
    }

    async function fetchTelemetrySummary() {
      requestId += 1
      const currentRequest = requestId
      isLoading.value = true
      resetCharts()
      try {
        const response = await store.dispatch(`${REPORTS_STORE_MODULE_NAME}/fetchTelemetrySummary`, {
          assetId,
          startDate: getDateUtc(dateStart.value),
          endDate: getDateUtc(dateEnd.value),
        })
        if (currentRequest !== requestId) return
        const telemetryData = response?.data?.data || []
        telemetryData.forEach(item => {
          Object.keys(chartConfigurations).forEach(key => {
            if (item[key] !== null && item[key] !== undefined) {
              chartConfigurations[key].yValues.push(item[key])
              chartConfigurations[key].xValues.push(item.deviceDateTime)
            }
          })
        })
      } catch (e) {
        if (currentRequest !== requestId) return
        showSnackbar({ title: $t('Error fetching report') })
      } finally {
        if (currentRequest === requestId) isLoading.value = false
      }
    }

    function refetchTelemetry() {
      if (!dateStart.value || !dateEnd.value) {
        showFilters.value = true
        return
      }
      ensureDateOrder()
      if (!validateDateRange(dateStart.value, dateEnd.value)) {
        showSnackbar({
          title: $t('Max interval seven days'),
          icon: 'InfoIcon',
          variant: 'info',
        })
        showFilters.value = true
        return
      }
      fetchTelemetrySummary()
    }

    function resetDateRange() {
      const range = buildDefaultRange()
      dateStart.value = range.start
      dateEnd.value = range.end
      dateRangePreset.value = 'last7'
      refetchTelemetry()
    }

    onMounted(() => {
      fetchAsset()
      fetchTelemetrySummary()
    })

    return {
      assetId,
      showFilters,
      isLoading,
      loadingAsset,
      dateStart,
      dateEnd,
      dateRangePreset,
      flatpickrConfig,
      assetPlate,
      assetChassis,
      assetManufacturer,
      assetModel,
      assetNickname,
      assetGroupName,
      assetCustomerName,
      assetAvatar,
      activeFiltersCount,
      periodLabel,
      periodPresets,
      visibleCharts,
      hasAnyChartData,
      setDatePreset,
      onCustomDateChange,
      refetchTelemetry,
      resetDateRange,
      copyToClipboard,
      handleImageError,
      goBack,
      goToAssetSummary,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.telemetry-summary-report-wrapper {
  .spin-icon {
    animation: telemetry-summary-spin 1s linear infinite;
  }

  @keyframes telemetry-summary-spin {
    100% {
      transform: rotate(360deg);
    }
  }

  .empty-copy {
    max-width: 520px;
  }

  .asset-header-card {
    border: 1px solid rgba(115, 103, 240, 0.14) !important;
    border-left: 4px solid #7367f0 !important;
    border-radius: 0.5rem;
    background-color: #fff;

    .asset-header-content {
      min-height: 64px;
      display: flex;
      align-items: center;
    }

    .back-btn {
      border: 1px solid rgba(0, 0, 0, 0.08);
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(115, 103, 240, 0.1) !important;
        color: #7367f0 !important;
        border-color: #7367f0;
      }
    }

    .asset-avatar-box {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      background: linear-gradient(135deg, #f8f8f8 0%, #eceef2 100%);
      border: 1px solid rgba(0, 0, 0, 0.06);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .asset-avatar-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 4px;
      }
    }

    .license-plate-badge {
      display: inline-flex;
      align-items: center;
      background: #fff;
      border: 2px solid #2b2b2b;
      border-radius: 6px;
      padding: 2px 8px;
      font-size: 0.95rem;
      font-weight: 800;
      letter-spacing: 1px;
      color: #2b2b2b;

      .plate-number {
        font-family: Consolas, 'Courier New', monospace;
      }

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
      }
    }

    .asset-brand-text {
      font-size: 1.05rem;
      color: #7367f0 !important;
    }

    .asset-model-title {
      font-size: 1.05rem;
    }

    .meta-chip {
      display: inline-flex;
      align-items: center;
      background: #f8f8f8;
      border: 1px solid #ebe9f1;
      border-radius: 20px;
      padding: 4px 10px;
      font-size: 0.8rem;

      .meta-label {
        font-weight: 600;
        color: #6e6b7b;
        margin-right: 4px;
      }

      .meta-value {
        font-weight: 600;
        color: #5e5873;
      }
    }
  }

  .filters-card {
    border-radius: 0.5rem;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.18s ease;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

body.dark-layout .telemetry-summary-report-wrapper {
  .asset-header-card {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    border-left-color: #7367f0 !important;

    .back-btn {
      border-color: rgba(255, 255, 255, 0.12);
      color: #b4b7bd;
    }

    .asset-avatar-box {
      background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
      border-color: rgba(255, 255, 255, 0.1);
    }

    .license-plate-badge {
      background: #161d31;
      border-color: #4b4b5a;
      color: #d0d2d6;
    }

    .asset-model-title {
      color: #d0d2d6 !important;
    }

    .meta-chip {
      background: #161d31;
      border-color: rgba(255, 255, 255, 0.08);

      .meta-label {
        color: #b4b7bd;
      }

      .meta-value {
        color: #d0d2d6;
      }
    }
  }
}
</style>
